import { useState } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@components/index';
import { FormInput } from '@components/FormInput/ui/FormInput';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthSelector } from '@providers/AuthProvider/ui/AuthProvider';
import { useLoading } from '@providers/index';
import { login } from '@api/auth/apiService';
import { LoginResponse } from '@api/auth/model/types.ts';
import toast from 'react-hot-toast';
import { Toast } from '@components/Toast/ui/Toast';
import { useSignUpFormContext } from '@providers/SignUpFormProvider/model/SignUpFormContext';
import { routes } from '@shared/constants/paths';
import { Grid, Stack, Theme, Typography, useMediaQuery } from '@mui/material';
import Divider from '@mui/material/Divider';

import styles from './SignInForm.module.scss';

interface SignInFormType {
  email: string;
  password: string;
}

/**
 * @description component for showing the Sign In authorization form
 */
function SignInForm() {
  const successLoginAction = useAuthSelector((state) => state.login);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const downMD = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const { setSignUpFormState } = useSignUpFormContext();

  const { setLoading } = useLoading();

  const navigate = useNavigate();

  const formMethods = useForm<SignInFormType>({
    mode: 'onSubmit',
    defaultValues: {
      email: '',
      password: '',
    },
    resolver: yupResolver(
      yup.object().shape({
        email: yup
          .string()
          .matches(
            /^[\w.-]+(\+[\w.-]+)?@[a-zA-Z0-9-]+\.[a-zA-Z]{2,4}$/,
            'Please enter a valid email'
          )
          .required('Please enter your email'),
        password: yup.string().required('Please enter your current password'),
      })
    ),
  });

  const onSubmit = async (loginData: any) => {
    setIsLoading(true);
    login(
      loginData,
      (data: LoginResponse) => {
        setIsLoading(false);
        successLoginAction(data);
        setLoading(true);
        navigate(routes.home);
      },
      (error) => {
        setIsLoading(false);
        // Prioritize message field (new format), fallback to error field for backward compatibility
        const errorCode = error?.response?.data?.error;
        const errorMessage = error?.response?.data?.message;

        // Check for USER_NOT_VERIFIED (old error code or new message)
        if (
          errorCode === 'USER_NOT_VERIFIED' ||
          (errorMessage &&
            errorMessage.includes(
              'verify your email address before signing in'
            ))
        ) {
          setSignUpFormState((prevState) => ({
            ...prevState,
            email: formMethods.getValues('email'),
          }));
          navigate('/verify-user');
        }
        // Check for BUSINESS_USER_NOT_APPROVED (old error code or new message)
        else if (
          errorCode === 'BUSINESS_USER_NOT_APPROVED' ||
          (errorMessage && errorMessage.includes('pending approval'))
        ) {
          toast(({ id }) => (
            <Toast
              id={id}
              type="error"
              title="Error"
              text={
                errorMessage || 'Your account is not yet approved by ZlaytAI.'
              }
            />
          ));
        }
        // Check for OTP required (new format with requires_otp flag)
        else if (error?.response?.data?.requires_otp === true) {
          setSignUpFormState((prevState) => ({
            ...prevState,
            email: formMethods.getValues('email'),
            password: formMethods.getValues('password'),
            otpSent: false, // OTP not sent yet, user will select method
          }));
          // Navigate directly to OTP method selection screen
          navigate('/verify-user-otp');
        }
        // Check for OTP_NOT_VERIFIED (old error code or legacy message - OTP already sent)
        else if (
          errorCode === 'OTP_NOT_VERIFIED' ||
          errorCode === 'CODE_NOT_VERIFIED' ||
          (errorMessage &&
            errorMessage.includes(
              'verification code has been sent to your email'
            ))
        ) {
          setSignUpFormState((prevState) => ({
            ...prevState,
            email: formMethods.getValues('email'),
            password: formMethods.getValues('password'),
            otpSent: true,
          }));
          // Show notification before navigating
          toast(({ id }) => (
            <Toast
              id={id}
              type="success"
              title="Verification Required"
              text={
                errorMessage ||
                'A verification code has been sent to your email. Please enter it to continue.'
              }
            />
          ));
          // Navigate after a short delay to allow user to see the notification
          setTimeout(() => {
            navigate('/verify-user-otp');
          }, 1500);
        }
        // Check for ACCOUNT_INACTIVE
        else if (errorCode === 'ACCOUNT_INACTIVE') {
          const message =
            errorMessage ||
            'Your account has been deactivated. Please contact support.';
          toast(({ id }) => (
            <Toast
              id={id}
              type="error"
              title="Account Inactive"
              text={message}
            />
          ));
        }
        // Default error handling
        else {
          toast(({ id }) => (
            <Toast
              id={id}
              type="error"
              title="Error"
              text={errorMessage || errorCode || 'Wrong email or password'}
            />
          ));
        }
      }
    );
  };

  return (
    <Grid container spacing={2} alignItems="center" justifyContent="center">
      <Grid item xs={12}>
        <Grid
          container
          direction={{ xs: 'column-reverse', md: 'row' }}
          alignItems="center"
          justifyContent="center"
        >
          <Grid item>
            <Stack alignItems="center" justifyContent="center" spacing={1}>
              <Typography
                color="gray.main"
                gutterBottom
                variant={downMD ? 'h3' : 'h2'}
              >
                Login
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <form onSubmit={formMethods.handleSubmit(onSubmit)}>
          <Stack display="flex" flexDirection="column" gap={3} paddingX={3}>
            <FormInput
              bordered
              type="input"
              labelText="Email"
              placeholder="name@company.com"
              errorText={
                formMethods.formState.errors?.email &&
                formMethods.formState.errors?.email.message
              }
              autoComplete
              {...formMethods.register('email')}
            />
            <FormInput
              bordered
              type="password"
              labelText="Password"
              placeholder="Enter your password"
              errorText={
                formMethods.formState.errors?.password &&
                formMethods.formState.errors?.password.message
              }
              {...formMethods.register('password')}
            />
            <div className={styles.forgotPassword}>
              <Link to="/forgot-password" className={styles.link}>
                Forgot password
              </Link>
            </div>
            <Button
              isLoading={isLoading}
              className={styles.signUpBtn}
              type="submit"
              disabled={isLoading}
              variant="active"
            >
              Login
            </Button>
          </Stack>
        </form>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <Grid item container direction="column" alignItems="center" xs={12}>
          <Stack
            display="flex"
            flexDirection="row"
            justifyContent="center"
            alignItems="center"
          >
            <Typography variant="subtitle1" sx={{ textDecoration: 'none' }}>
              Don&apos;t have an account?
            </Typography>
            <Link to="/sign-up">
              <Button variant="underline">Sign up</Button>
            </Link>
          </Stack>
        </Grid>
      </Grid>
    </Grid>
  );
}

export default SignInForm;
